"use client";
// Client-side component: runs in the browser and uses React hooks

import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import type { FetchBrandsResponse } from "@/app/utilities/interfaces";
import { getModelsByBrandID, getVariantList, searchCarsAndBrands } from "@/app/utilities/apis";
import { getLocationCookie } from "@/app/utilities/locationCookie";
import { getOnRoadPrice } from "@/utils/onRoadPrice";
import EmiCalculator from "./EmiCalculator";
import {
    calculateEMI,
    EMI_DEFAULT_INTEREST_RATE,
    EMI_DEFAULT_TENURE,
    formatEmiCurrency,
    getDefaultDownPayment,
    resolveDefaultEmiVariant,
} from "@/lib/utils/emi";
import ImageWrapper from "../ImageWrapper";
import type { LocationSuggestion, CityData } from "@/types/city";
import { EMICALCULATOR_POPUP_TRACKING } from "@/lib/constants/tracking";
import Image from "next/image";
import { formatPrice } from "@/app/utilities/helper";
import {
    EMI_CALCULATOR_METRICS_EVENT,
    EMI_SELECTED_VARIANT_EVENT,
    type EmiCalculatorMetricsDetail,
} from "@/component/ui/CityPriceGrid/CityPriceGridReactive";
import { Heading, InfoToolCard } from "@farm-junction/design-library";

function EmiSummaryChevronIcon() {
    const gradientId = useId();

    return (
        <svg width="26" height="9" viewBox="0 0 26 9" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path
                d="M20.6288 9H17.1576C16.9073 9 16.7813 8.69681 16.9585 8.51962L20.9781 4.5L16.9579 0.479813C16.7808 0.303188 16.9068 0 17.1571 0H20.6283C20.8521 0 21.0664 0.0888745 21.2251 0.246937L25.0804 4.10231C25.3004 4.32225 25.3004 4.67831 25.0804 4.89769L21.2256 8.7525C21.067 8.91112 20.8527 9 20.6288 9ZM4.35007 8.7525L8.20489 4.89769C8.42482 4.67775 8.42482 4.32169 8.20489 4.10231L4.34951 0.246937C4.19145 0.0888745 3.97714 0 3.75326 0H0.282073C0.0317605 0 -0.0942395 0.303188 0.082948 0.480375L4.10257 4.5L0.082948 8.51962C-0.0942395 8.69681 0.031198 9 0.282073 9H3.75326C3.97714 9 4.19201 8.91112 4.35007 8.7525ZM12.7876 8.7525L16.6424 4.89769C16.8623 4.67775 16.8623 4.32169 16.6424 4.10231L12.787 0.246937C12.6295 0.0888745 12.4146 0 12.1908 0H8.71957C8.46926 0 8.34326 0.303188 8.52045 0.480375L12.5406 4.5L8.52101 8.51962C8.34382 8.69681 8.46926 9 8.71957 9H12.1908C12.4152 9 12.6295 8.91112 12.7876 8.7525Z"
                fill={`url(#${gradientId})`}
                fillOpacity={0.26}
            />
            <defs>
                <linearGradient id={gradientId} x1="25.2454" y1="4.5" x2="0" y2="4.5" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#00A63E" />
                    <stop offset="1" stopColor="#00A63E" stopOpacity={0.24} />
                </linearGradient>
            </defs>
        </svg>
    );
}

interface BrandOption {
    _id: string;
    name: string;
    slug?: string;
    image?: string;
    is_popular?: boolean;
    is_active?: boolean;
}

interface ModelOption {
    _id: string;
    name: string;
    slug: string;
    detailed_slug?: string;
    brand_id?: string | { _id?: string; name?: string; slug?: string };
    brand_name?: string;
    brand_slug?: string;
    featured_image?: string | null;
}

interface SpecEntry {
    key: string;
    label: string;
    value: string | number | boolean;
    valueType?: string;
    unit?: string;
    sortOrder: number;
    _id: string;
}

interface SpecSection {
    key: string;
    label: string;
    order: number;
    specs: SpecEntry[];
    _id: string;
}

interface VariantOption {
    _id: string;
    name: string;
    slug: string;
    price: number;
    fuel_type?: string;
    transmission?: string;
    images?: string[];
    featured_image?: string;
    specSections?: SpecSection[];
    is_electric?: boolean;
    is_active?: boolean;
    is_sanity_done?: boolean;
    is_discontinued?: boolean;
    status?: string | null;
    is_base_variant?: boolean;
    is_top_variant?: boolean;
    fuel_types?: string[];
    transmissions?: string[];
    seating_capacity?: number;
    brand?: {
        _id: string;
        name: string;
        slug: string;
    };
    model?: {
        _id: string;
        name: string;
        slug: string;
        detailed_slug: string;
        featured_image?: string;
    };
}

interface SearchResponseModel extends ModelOption {
    title?: string;
    display_name?: string;
    image?: string;
}

interface EmiCalculatorPageClientProps {
    heading?: string;
    isMobile?: boolean;
    isEmiRoute?: boolean;
    isPriceInCityRoute?: boolean;
    brands: FetchBrandsResponse["data"];
    initialBrand?: BrandOption | null;
    initialModel?: ModelOption | null;
    defaultLocation?: CityData | null;
    initialVariants?: any[];
    initialSelectedVariantId?: string;
    VarentSectorClass?: string;
    DownPaymentClass?: string;
    LoanTermClass?: string;
    InterestRateClass?: string;
    EMILocationClass?: string;
    mobileRevampLayout?: boolean;
    offerButton?: ReactNode;
    hideRevampHeader?: boolean;
    hideRevampOfferButton?: boolean;
    externalSelectedVariantId?: string;
    onLiveMetricsChange?: (metrics: {
        emi: number;
        loanMonths: number;
        effectivePrice: number;
        interestRate: number;
        downPayment: number;
        loanAmount: number;
    }) => void;
}

const EMPTY_VARIANTS: any[] = [];

// Build URL for the EMI calculator route for a given brand/model/variant
function buildEmiCalculatorUrl(
    brand: BrandOption | null,
    model: ModelOption | null,
    variant?: VariantOption | null,
) {
    const brandSlug = brand?.slug?.trim();
    const modelSlug = model?.slug?.trim();

    if (!brandSlug || !modelSlug) {
        return "/emi-calculator";
    }

    const query = variant?.slug ? `?variant=${variant.slug}` : "";
    return `/${brandSlug}/${modelSlug}/car-loan-emi-calculator${query}`;
}

type SelectorStep = "brand" | "model" | "variant";

// Normalize models or API responses into a consistent array shape
function normalizeModels(payload: unknown): ModelOption[] {
    if (Array.isArray(payload)) {
        return payload as ModelOption[];
    }

    if (payload && typeof payload === "object" && "data" in payload) {
        const data = (payload as { data?: unknown }).data;
        return Array.isArray(data) ? (data as ModelOption[]) : [];
    }

    return [];
}

// Normalize variants or API responses into a consistent array shape
function normalizeVariants(payload: unknown): VariantOption[] {
    if (Array.isArray(payload)) {
        return payload as VariantOption[];
    }

    if (payload && typeof payload === "object" && "data" in payload) {
        const data = (payload as { data?: unknown }).data;
        return Array.isArray(data) ? (data as VariantOption[]) : [];
    }

    return [];
}

// Build a descriptive string for a given car variant by extracting key specifications such as engine displacement, fuel type, and transmission type, and concatenating them in a readable format.
function getVariantMeta(variant: VariantOption) {
    const fuel = variant.fuel_type || variant.fuel_types?.[0] || "";
    const transmission = variant.transmission || variant.transmissions?.[0] || "";

    const engine = (variant.specSections ?? []).reduce((acc: SpecEntry[], section) => acc.concat(section.specs ?? []), []).find(
        (spec) => spec.key === "engine_displacement" || spec.key === "engine",
    )?.value;

    return [engine ? String(engine) : "", fuel, transmission].filter(Boolean).join(" • ");
}



/**
 * Normalize a car variant object to ensure it has all the necessary fields for the EMI calculator, 
 * providing default values for any missing properties and flattening nested brand and model information for easier access.
 */
function normalizeVariantForCalculator(variant: VariantOption): VariantOption & {
    fuel_type: string;
    transmission: string;
    images: string[];
    featured_image: string;
    specSections: SpecSection[];
    is_electric: boolean;
    is_active: boolean;
    is_base_variant: boolean;
    is_top_variant: boolean;
    fuel_types: string[];
    transmissions: string[];
    seating_capacity: number;
    brand: { _id: string; name: string; slug: string };
    model: { _id: string; name: string; slug: string; detailed_slug: string; featured_image: string };
} {
    return {
        ...variant,
        fuel_type: variant.fuel_type ?? "",
        transmission: variant.transmission ?? "",
        images: variant.images ?? [],
        featured_image: variant.featured_image ?? "",
        specSections: variant.specSections ?? [],
        is_electric: variant.is_electric ?? false,
        is_active: variant.is_active ?? true,
        is_base_variant: variant.is_base_variant ?? false,
        is_top_variant: variant.is_top_variant ?? false,
        fuel_types: variant.fuel_types ?? [],
        transmissions: variant.transmissions ?? [],
        seating_capacity: variant.seating_capacity ?? 0,
        brand: variant.brand ?? { _id: "", name: "", slug: "" },
        model: {
            _id: variant.model?._id ?? "",
            name: variant.model?.name ?? "",
            slug: variant.model?.slug ?? "",
            detailed_slug: variant.model?.detailed_slug ?? "",
            featured_image: variant.model?.featured_image ?? "",
        },
    };
}

/**
 * The EmiCalculatorPageClient component provides an interface for users to select their car's brand, model, and 
 * variant, and then calculates the EMI based on the selected variant's price and the on-road price in the user's 
 * city. It manages the state of the selection process, handles API calls to fetch models and variants, and updates the URL to reflect the current selection.
 */
export default function EmiCalculatorPageClient({
    heading,
    isMobile = false,
    brands,
    initialBrand = null,
    initialModel = null,
    initialVariants = EMPTY_VARIANTS,
    initialSelectedVariantId,
    isEmiRoute = false,
    isPriceInCityRoute = false,
    defaultLocation = null,
    VarentSectorClass,
    DownPaymentClass,
    LoanTermClass,
    InterestRateClass,
    EMILocationClass,
    mobileRevampLayout = false,
    offerButton,
    hideRevampHeader = false,
    hideRevampOfferButton = false,
    externalSelectedVariantId,
    onLiveMetricsChange,
}: EmiCalculatorPageClientProps) {
    const router = useRouter();
    const [showSelector, setShowSelector] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState<BrandOption | null>(initialBrand);
    const [selectedModel, setSelectedModel] = useState<ModelOption | null>(initialModel);
    const [models, setModels] = useState<ModelOption[]>([]);
    const [variants, setVariants] = useState<VariantOption[]>(initialVariants);
    const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(initialSelectedVariantId);
    const [brandSearch, setBrandSearch] = useState("");
    const [modelSearch, setModelSearch] = useState("");
    const [variantSearch, setVariantSearch] = useState("");
    const [searchResultBrands, setSearchResultBrands] = useState<BrandOption[]>([]);
    const [searchResultModels, setSearchResultModels] = useState<SearchResponseModel[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [cityName, setCityName] = useState("");
    const [cityData, setCityData] = useState<CityData | null>(null);
    const [onRoadPrices, setOnRoadPrices] = useState<Record<string, number>>({});
    const [liveEmiMetrics, setLiveEmiMetrics] = useState({
        emi: 0,
        loanMonths: EMI_DEFAULT_TENURE,
        effectivePrice: 0,
        interestRate: EMI_DEFAULT_INTEREST_RATE,
        downPayment: 0,
        loanAmount: 0,
    });
    const modalRef = useRef<HTMLDivElement | null>(null);
    const snapshotRef = useRef<{
        selectedBrand: BrandOption | null;
        selectedModel: ModelOption | null;
        models: ModelOption[];
        variants: VariantOption[];
        selectedVariantId?: string;
    } | null>(null);

    const selectedVariant = useMemo(
        () => resolveDefaultEmiVariant(variants, selectedVariantId),
        [selectedVariantId, variants],
    );

    // Broadcast variant changes so components rendered elsewhere on the page
    // (e.g. CityPriceGridReactive, positioned further down the page like on
    // sibling PDP pages rather than right next to the calculator) can stay
    // in sync without needing to be nested inside this component's tree.
    useEffect(() => {
        if (!selectedVariant) return;
        window.dispatchEvent(new CustomEvent(EMI_SELECTED_VARIANT_EVENT, { detail: selectedVariant }));
    }, [selectedVariant]);

    const step: SelectorStep = selectedModel ? "variant" : selectedBrand ? "model" : "brand";

    const popularBrands = useMemo(
        () => brands.filter((item) => item.is_popular).sort((left, right) => left.name.localeCompare(right.name)),
        [brands],
    );

    const allBrands = useMemo(
        () => [...brands].sort((left, right) => left.name.localeCompare(right.name)),
        [brands],
    );

    const filteredModels = useMemo(
        () => models.filter((item) => item.name.toLowerCase().includes(modelSearch.toLowerCase())),
        [modelSearch, models],
    );

    const filteredVariants = useMemo(
        () => variants.filter((item) => item.is_sanity_done !== false && item.status !== 'discontinued' && item.name.toLowerCase().includes(variantSearch.toLowerCase())),
        [variantSearch, variants],
    );

    const calculatorVariants = useMemo(
        () => variants.filter((item) => item.is_sanity_done !== false && item.status !== 'discontinued').map(normalizeVariantForCalculator),
        [variants],
    );

    const selectedCarName = selectedBrand && selectedModel
        ? `${selectedBrand.name} ${selectedModel.name}`.trim()
        : undefined;
    // On the brand/model price-in-city page the model is already shown in the
    // page header, so drop it here (keep the brand) to avoid repeating it.
    const selectedVehicleLabel = selectedVariant && selectedCarName
        ? `${isPriceInCityRoute ? selectedBrand?.name : selectedCarName} ${selectedVariant.name}`.trim()
        : "Select Your Vehicle";
    const selectedOnRoadPrice = selectedVariant?._id ? onRoadPrices[selectedVariant._id] : undefined;
    const selectedVariantPrice = selectedOnRoadPrice ?? selectedVariant?.price ?? 0;
    const defaultDownPayment = getDefaultDownPayment(selectedVariantPrice);
    const defaultEmi = calculateEMI({
        price: selectedVariantPrice,
        downPayment: defaultDownPayment,
        interestRate: EMI_DEFAULT_INTEREST_RATE,
        tenureMonths: EMI_DEFAULT_TENURE,
    }).emi;

    useEffect(() => {
        setLiveEmiMetrics((current) => ({
            ...current,
            emi: defaultEmi,
            effectivePrice: selectedVariantPrice,
        }));
    }, [defaultEmi, selectedVariantPrice]);

    const activeCalculatorVariants = useMemo(
        () => variants.filter((item) => item.is_sanity_done !== false && item.status !== "discontinued"),
        [variants],
    );

    const selectorCardClass =
        "rounded-[12px] border border-[#D8DEE8] bg-[#FAFAFA] px-4 py-[10px] [&_svg]:!h-[10px] [&_svg]:!w-[10px] [&_svg]:!min-h-[10px] [&_svg]:!min-w-[10px]";
    const selectorHeadingClass =
        "!mb-0 !text-[11px] !leading-[14px] !font-normal !tracking-normal !text-[#8B93A0]";
    const selectorValueClass = "!text-[14px] !font-semibold !leading-[18px] !text-[#101828]";
    const selectorLinkClass =
        "!flex !w-full !items-center !justify-between !gap-2 !font-normal !text-inherit hover:!text-inherit";
    const emiSummaryCardClass =
        "flex min-h-[56px] min-w-0 flex-1 flex-col justify-center overflow-hidden rounded-[12px] border-0 px-3 py-3 shadow-none";
    const emiSummaryPriceClass = "!text-[15px] !font-bold !leading-5 !text-[#101828]";
    const emiSummaryEmiClass = "!text-[15px] !font-bold !leading-5 !text-[#00A63E]";

    useEffect(() => {
        setSelectedBrand(initialBrand ?? null);
        setSelectedModel(initialModel ?? null);
        setVariants(initialVariants);
        setSelectedVariantId(initialSelectedVariantId);
        setModels([]);
        setErrorMessage(null);
        setBrandSearch("");
        setModelSearch("");
        setVariantSearch("");
    }, [initialBrand, initialModel, initialSelectedVariantId, initialVariants]);

    useEffect(() => {
        if (!externalSelectedVariantId) return;
        setSelectedVariantId(externalSelectedVariantId);
    }, [externalSelectedVariantId]);

    useEffect(() => {
        onLiveMetricsChange?.(liveEmiMetrics);
    }, [liveEmiMetrics, onLiveMetricsChange]);

    useEffect(() => {
        if (!showSelector) {
            return;
        }

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeSelector();
            }
        };

        const handleOutsideClick = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                closeSelector();
            }
        };

        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", handleEscape);
        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.body.style.overflow = "";
            document.removeEventListener("keydown", handleEscape);
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [showSelector]);

    // Initial city resolution on component mount and setup listener for location updates
    useEffect(() => {
        let mounted = true;

        const resolveCity = async () => {
            // Always fetch the latest location from cookie when called
            const latestLocation = getLocationCookie ? getLocationCookie() : null;
            let cityDataFull = null;

            // If we have a slug, fetch full city data (with RTO etc.)
            if (latestLocation && latestLocation.slug) {
                try {
                    const cityApi = (await import("@/app/utilities/apis"));
                    cityDataFull = await cityApi.getCityBySlug(latestLocation.slug);
                } catch (err) {
                    cityDataFull = null;
                }
            }

            // Fallback to defaultLocation if nothing in cookie
            if (!cityDataFull && (!latestLocation || Object.keys(latestLocation).length === 0)) {
                cityDataFull = defaultLocation && Object.keys(defaultLocation).length > 0 ? defaultLocation : null;
            }

            if (!mounted) return;
            setCityData(cityDataFull);
            // setCityName(cityDataFull ? `${cityDataFull?.name}, ${cityDataFull?.state?.name}` : "");
            setCityName(cityDataFull ? `${cityDataFull?.name}` : "");
        };

        const handleLocationUpdated = () => {
            resolveCity();
        };

        resolveCity();
        window.addEventListener("location-updated", handleLocationUpdated);

        return () => {
            mounted = false;
            window.removeEventListener("location-updated", handleLocationUpdated);
        };
    }, []);

    // Recalculate on-road prices whenever city data or variants change
    useEffect(() => {
        if (!cityData || variants.length === 0) {
            setOnRoadPrices({});
            return;
        }

        const nextPrices = variants.reduce((acc: Record<string, number>, item) => {
            const result = getOnRoadPrice(item, cityData);
            if (item._id && result) {
                acc[item._id] = result.onRoadPrice;
            }
            return acc;
        }, {});

        setOnRoadPrices(nextPrices);
    }, [cityData, variants]);

    // Search for brands and models based on user input in the brand search field
    useEffect(() => {
        if (!showSelector || step !== "brand" || brandSearch.trim().length < 2) {
            setSearchResultBrands([]);
            setSearchResultModels([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        const timer = window.setTimeout(async () => {
            try {
                const { brandsList, productList } = await searchCarsAndBrands(brandSearch.trim());
                setSearchResultBrands(Array.isArray(brandsList) ? brandsList : []);
                setSearchResultModels(Array.isArray(productList) ? productList : []);
            } catch {
                setSearchResultBrands([]);
                setSearchResultModels([]);
            } finally {
                setIsSearching(false);
            }
        }, 250);

        return () => {
            window.clearTimeout(timer);
        };
    }, [brandSearch, showSelector, step]);

    const loadModels = async (brand: BrandOption) => {
        setIsLoading(true);
        setErrorMessage(null);

        try {
            const response = await getModelsByBrandID({
                brandId: brand._id,
                page: 1,
                limit: 100,
                is_discontinued: false
            });
            const nextModels = normalizeModels(response);
            setModels(nextModels);
            if (nextModels.length === 0) {
                setErrorMessage("No models are available for this brand right now.");
            }
        } catch {
            setModels([]);
            setErrorMessage("Failed to load models. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const loadVariants = async (model: ModelOption) => {
        setIsLoading(true);
        setErrorMessage(null);

        try {
            const response = await getVariantList(model._id, 1, 100, "asc");
            const nextVariants = normalizeVariants(response);
            setVariants(nextVariants);

            if (nextVariants.length > 0) {
                setSelectedVariantId(nextVariants[0]._id);
            } else {
                setSelectedVariantId(undefined);
                setErrorMessage("No variants are available for this model right now.");
            }
        } catch {
            setVariants([]);
            setSelectedVariantId(undefined);
            setErrorMessage("Failed to load variants. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleBrandSelect = async (brand: BrandOption) => {
        setSelectedBrand(brand);
        setSelectedModel(null);
        setModels([]);
        setVariants([]);
        setSelectedVariantId(undefined);
        setModelSearch("");
        setVariantSearch("");
        await loadModels(brand);
    };

    const handleModelSelect = async (model: ModelOption) => {
        setSelectedModel(model);
        setVariants([]);
        setSelectedVariantId(undefined);
        setVariantSearch("");
        await loadVariants(model);
    };

    const handleSearchModelSelect = async (model: SearchResponseModel) => {

        const brandSlugFromModel = model?.brand_slug || "";
        const brandNameFromModel = model?.brand_name || "";

        const matchedBrand = brands.find((item) => (brandSlugFromModel && item.slug === brandSlugFromModel) || (brandNameFromModel && item.name === brandNameFromModel)) ?? {
            _id: model?.brand_slug || "",
            name: model?.brand_name || "",
            slug: model?.brand_slug || "",
            image: "",
            is_popular: false,
        };

        const normalizedModel: ModelOption = {
            _id: model._id,
            name: model.name || model.title || model.display_name || "",
            slug: model.slug || "",
            detailed_slug: model.detailed_slug || model.slug || "",
            brand_name: model?.brand_name || "",
            brand_slug: model?.brand_slug || "",
            featured_image: model.featured_image || model.image || null,
        };

        setSelectedBrand(matchedBrand);
        setSelectedModel(normalizedModel);
        setModels([]);
        setVariants([]);
        setSelectedVariantId(undefined);
        setModelSearch("");
        setVariantSearch("");
        await loadVariants(normalizedModel);
    };

    const handleVariantSelect = (variantId: string) => {
        const nextVariant = variants.find((item) => item._id === variantId) ?? null;
        setSelectedVariantId(variantId);
        closeSelector(true);
        if (selectedModel?.slug && nextVariant?.slug) {
            document.cookie = `_selVariant=${encodeURIComponent(`${selectedModel.slug}:${nextVariant.slug}`)};path=/;max-age=${60 * 60 * 24 * 30}`;
        }
        if(!isPriceInCityRoute){
            router.push(buildEmiCalculatorUrl(selectedBrand, selectedModel, nextVariant));
        }  
    };

    const openSelector = () => {
        snapshotRef.current = {
            selectedBrand,
            selectedModel,
            models,
            variants,
            selectedVariantId,
        };
        setErrorMessage(null);
        setShowSelector(true);
    };

    const closeSelector = (shouldCommit: boolean = false) => {
        if (!shouldCommit && snapshotRef.current) {
            setSelectedBrand(snapshotRef.current.selectedBrand);
            setSelectedModel(snapshotRef.current.selectedModel);
            setModels(snapshotRef.current.models);
            setVariants(snapshotRef.current.variants);
            setSelectedVariantId(snapshotRef.current.selectedVariantId);
            setErrorMessage(null);
        }

        setBrandSearch("");
        setModelSearch("");
        setVariantSearch("");
        setSearchResultBrands([]);
        setSearchResultModels([]);
        setShowSelector(false);
    };

    const goBackToBrand = () => {
        setSelectedBrand(null);
        setSelectedModel(null);
        setModels([]);
        setVariants([]);
        setSelectedVariantId(undefined);
        setErrorMessage(null);
        setModelSearch("");
        setVariantSearch("");
    };

    const goBackToModel = async () => {
        if (!selectedBrand) {
            goBackToBrand();
            return;
        }

        setSelectedModel(null);
        setVariants([]);
        setSelectedVariantId(undefined);
        setVariantSearch("");
        await loadModels(selectedBrand);
    };

    const showRevampMobile = Boolean(isMobile && mobileRevampLayout);

    const handleEmiMetricsChange = useCallback(
        (metrics: EmiCalculatorMetricsDetail) => {
            if (showRevampMobile) {
                setLiveEmiMetrics(metrics);
            }
            onLiveMetricsChange?.(metrics);
            if (isEmiRoute) {
                window.dispatchEvent(new CustomEvent(EMI_CALCULATOR_METRICS_EVENT, { detail: metrics }));
            }
        },
        [isEmiRoute, onLiveMetricsChange, showRevampMobile],
    );

    return (
        <>
            <div
                className={
                    showRevampMobile
                        ? ""
                        : "rounded-[20px] border border-[#d7e3f1] bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]"
                }
            >
                {showRevampMobile ? (
                    <>
                        <Heading
                            variant="h2"
                            level={2}
                            color="black"
                            fontWeight="bold"
                            className="!mb-4 !text-[20px] !leading-[26px] !tracking-normal !text-[#101828]"
                        >
                            {heading ? heading : "Calculate Your Car Loan EMI"}
                        </Heading>

                        {!hideRevampHeader ? (
                        <div className="relative mb-4 grid grid-cols-2 gap-2.5">
                            <InfoToolCard
                                title={`Variants (${activeCalculatorVariants.length})`}
                                description={selectedVariant?.name?.trim() || "Select variant"}
                                href="#variants"
                                showImage={false}
                                className={selectorCardClass}
                                slotProps={{
                                    headingProps: { variant: "p", level: "p", color: "tertiary", className: selectorHeadingClass },
                                    descriptionProps: { isTruncated: false, className: selectorValueClass },
                                    linkProps: {
                                        title: selectedVariant?.name || "Select variant",
                                        className: `${selectorLinkClass} ${EMICALCULATOR_POPUP_TRACKING.VARIANT_SELECTOR_CLICK}`,
                                        onClick: (event) => {
                                            event.preventDefault();
                                            openSelector();
                                        },
                                    },
                                }}
                            />
                            <InfoToolCard
                                title="Location"
                                description={cityName || "Select city"}
                                href="#location"
                                showImage={false}
                                className={selectorCardClass}
                                slotProps={{
                                    headingProps: { variant: "p", level: "p", color: "tertiary", className: selectorHeadingClass },
                                    descriptionProps: { isTruncated: false, className: selectorValueClass },
                                    linkProps: {
                                        title: cityName,
                                        className: `${selectorLinkClass} ${EMICALCULATOR_POPUP_TRACKING.EMI_LOCATION_CLICK}`,
                                        onClick: (event) => {
                                            event.preventDefault();
                                            window.dispatchEvent(new CustomEvent("open-location-modal"));
                                        },
                                    },
                                }}
                            />
                        </div>
                        ) : null}

                        {!hideRevampHeader && selectedVariant ? (
                            <div className="mb-4 flex items-stretch gap-1.5">
                                <div
                                    className={`${emiSummaryCardClass} min-w-0 flex-1 !flex-row !items-center !justify-between gap-2 bg-gradient-to-l from-white to-[#F5F5F5]`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className={selectorHeadingClass}>{`On-road price · ${cityName}`}</p>
                                        <p className={`mt-1 ${emiSummaryPriceClass}`}>
                                            ₹{formatEmiCurrency(liveEmiMetrics.effectivePrice || selectedVariantPrice)}
                                        </p>
                                    </div>
                                    <div
                                        className="flex shrink-0 items-center justify-center"
                                    >
                                        <EmiSummaryChevronIcon />
                                    </div>
                                </div>
                                <div className={`${emiSummaryCardClass} min-w-0 flex-1 bg-[#EEFFF4]`}>
                                    <p className={selectorHeadingClass}>{`EMI · ${liveEmiMetrics.loanMonths} months`}</p>
                                    <p className="mt-1">
                                        <span className={emiSummaryEmiClass}>
                                            ₹{formatEmiCurrency(liveEmiMetrics.emi || defaultEmi)}
                                        </span>
                                        <span className="text-[12px] font-normal leading-5 text-[#8B93A0]"> /month</span>
                                    </p>
                                </div>
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div className="">
                        <h2 className="text-[17px] font-bold text-[#1f2937] md:text-[18px]">{heading ? heading : "Calculate Your Car Loan EMI"}</h2>
                        {isPriceInCityRoute && selectedModel?.name && selectedVariant ? (
                            <p className="mt-2 text-[13px] leading-5 text-[#4b5563] md:text-[14px]">
                                For a 5-year loan tenure, {formatEmiCurrency(defaultDownPayment)} down payment and 10% interest rate, EMI for {selectedModel.name} {selectedVariant.name} will be: ₹{formatEmiCurrency(defaultEmi)}/Month
                            </p>
                        ) : null}

                        <button
                            type="button"
                            onClick={openSelector}
                            className={`mt-3 flex w-full items-center justify-between rounded-[10px] border border-[#d2def0] bg-[#f3f7ff] px-3.5 py-3 text-left transition hover:border-[#b8cae2] hover:bg-[#eef4ff] ${EMICALCULATOR_POPUP_TRACKING.VARIANT_SELECTOR_CLICK}`}
                        >
                            <span className={`text-[15px] font-medium ${selectedVariant ? "text-[#1f3b73]" : "text-[#2563eb]"}`}>
                                {selectedVehicleLabel}
                            </span>
                            <svg width="16" height="16" viewBox="0 0 24 24" className="text-blue-500">
                                <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="3" />
                            </svg>
                        </button>

                        {selectedVariant && (
                            <div className="mt-3 flex items-center justify-between gap-3 pb-3 text-sm">
                                <button
                                    type="button"
                                    onClick={() => window.dispatchEvent(new CustomEvent("open-location-modal"))}
                                    className={`flex items-center gap-1.5 text-left text-[#4b5563] ${EMICALCULATOR_POPUP_TRACKING.EMI_LOCATION_CLICK}`}
                                >
                                    <span>
                                        On-Road Price in <span className="font-semibold text-[#2563eb]">{cityName}</span>
                                    </span>
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/edit-pencil.svg`}
                                        alt="edit-pencil"
                                        title="edit-pencil"
                                        width={14}
                                        height={14}
                                        style={{
                                            filter:
                                                "brightness(0) saturate(100%) invert(34%) sepia(83%) saturate(1647%) hue-rotate(190deg) brightness(92%) contrast(140%)",
                                        }}
                                    />
                                </button>

                                <span className="text-[15px] font-bold text-[#111827] md:text-[16px]">
                                    ₹{formatEmiCurrency(selectedOnRoadPrice ?? selectedVariant.price ?? 0)}
                                </span>
                            </div>
                        )}
                        {!selectedVariant && <div className="mb-2 h-6" />}
                    </div>
                )}

                <EmiCalculator
                    carName={selectedCarName}
                    variants={calculatorVariants}
                    onRoadPrices={onRoadPrices}
                    cityName={cityName}
                    selectedVariantId={selectedVariantId}
                    onVariantChange={(variant) => setSelectedVariantId(variant._id)}
                    isMobile={isMobile}
                    hideHeader={true}
                    hideVariantSelector={true}
                    isOpen={true}
                    isEmiRoute={isEmiRoute}
                    mobileRevampLayout={showRevampMobile}
                    onMetricsChange={isEmiRoute || showRevampMobile ? handleEmiMetricsChange : undefined}
                    VarentSectorClass={EMICALCULATOR_POPUP_TRACKING.VARIANT_SELECTOR_CLICK}
                    DownPaymentClass={EMICALCULATOR_POPUP_TRACKING.DOWN_PAYMENT_CLICK}
                    LoanTermClass={EMICALCULATOR_POPUP_TRACKING.LOAN_PERIOD_CLICK}
                    InterestRateClass={EMICALCULATOR_POPUP_TRACKING.INTEREST_RATE_CLICK}
                />

                {showRevampMobile && offerButton && !hideRevampOfferButton ? <div className="mt-4">{offerButton}</div> : null}
            </div>

            {showSelector && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div
                        ref={modalRef}
                        className="bg-white rounded-lg shadow-xl w-full max-w-lg border border-cyan-500 h-[500px] flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-6 pb-3">
                            <div className="flex justify-between items-center mb-2">
                                <h2 className="text-xl font-bold text-gray-900">
                                    {step === "brand"
                                        ? "Select Brand"
                                        : step === "model"
                                            ? `Select ${selectedBrand?.name ?? ""} Model`
                                            : `Select ${selectedModel?.name ?? ""} Variant`}
                                </h2>
                                <button
                                    type="button"
                                    onClick={() => closeSelector()}
                                    aria-label="Close vehicle selector"
                                    className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                                >
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`} 
                                        alt="search"
                                        title="cross"
                                        width={16} 
                                        height={16}
                                        className="w-[16px] h-[16px] invert-[45%] brightness-[70%]"
                                    />
                                </button>
                            </div>

                            {/* Breadcrumbs */}
                            <div className="flex items-center text-sm text-gray-600">
                                <button
                                    type="button"
                                    onClick={goBackToBrand}
                                    className="flex items-center gap-1 font-medium text-green-600 hover:text-cyan-600 transition cursor-pointer"
                                >
                                    {step !== "brand" && (
                                        <Image
                                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}right-arrow.svg`}
                                            alt="right-arrow"
                                            width={18}
                                            height={18}
                                            className="inline-block mr-1"
                                            style={{
                                            filter:
                                                "invert(48%) sepia(91%) saturate(375%) hue-rotate(108deg) brightness(94%) contrast(94%)"
                                            }}
                                        />
                                    )}
                                    Brand
                                </button>
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg`} 
                                    alt="search"
                                    width={8} 
                                    height={8}
                                    className="w-[8px] h-[8px] invert-[60%] brightness-[70%] mx-1"
                                />
                                <button
                                    type="button"
                                    onClick={() => step === "variant" ? goBackToModel() : undefined}
                                    disabled={step === "brand"}
                                    className={`flex items-center gap-1 font-medium transition ${step === "model" || step === "variant"
                                        ? "text-green-600 hover:text-cyan-600 cursor-pointer"
                                        : "text-gray-400 cursor-not-allowed"
                                        }`}
                                >
                                    {step === "variant" && (
                                        <Image
                                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}right-arrow.svg`} // or right-arrow.svg
                                            alt="check"
                                            width={14}
                                            height={14}
                                            className="inline-block"
                                            style={{
                                            filter:
                                                "invert(48%) sepia(77%) saturate(378%) hue-rotate(85deg) brightness(95%) contrast(92%)"
                                            }}
                                        />
                                    )}
                                    Model
                                </button>
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg`} 
                                    alt="search"
                                    width={8} 
                                    height={8}
                                    className="w-[8px] h-[8px] invert-[60%] brightness-[70%] mx-1"
                                />
                                <span
                                    className={`flex items-center gap-1 font-medium ${step === "variant" ? "text-green-600" : "text-gray-400"
                                        }`}
                                >
                                    Variant
                                </span>
                            </div>
                        </div>

                        <hr className="border-gray-200" />
                        <div className="flex-1 overflow-y-auto p-2">
                            {isLoading && (
                                <div className="flex justify-center py-8">
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/Loader2.svg`}
                                        alt="loader"
                                        width={24}
                                        height={24}
                                        className="animate-spin"                                        
                                        />
                                </div>
                            )}

                            {!isLoading && errorMessage && (
                                <div className="mx-4 my-2 p-3 rounded-md border border-red-100 bg-red-50 text-red-800">
                                    <div className="flex items-start gap-2">
                                        <Image
                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`} 
                                        alt="search"
                                        title="cross"
                                        width={16} 
                                        height={16}
                                        className="w-[16px] h-[16px] invert-[45%] brightness-[70%] flex-shrink-0 mt-0.5"
                                    />
                                        <div className="text-sm">{errorMessage}</div>
                                    </div>
                                </div>
                            )}

                            {/* === BRAND STEP === */}
                            {!isLoading && !errorMessage && step === "brand" && (
                                <>
                                    <div className="py-4">
                                        <div className="relative">
                                            <Image
                                                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/search.svg`} 
                                                alt="search"
                                                title="search"
                                                width={18} 
                                                height={18}
                                                className="absolute left-3 top-2 text-[#99A1AF] w-[18px] h-[18px] z-10 invert-[60%] brightness-[70%]"
                                            />

                                            <input
                                                type="text"
                                                value={brandSearch}
                                                onChange={(event) => setBrandSearch(event.target.value)}
                                                placeholder="Search Brands & Models"
                                                className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                                            />
                                        </div>
                                    </div>

                                    {brandSearch.trim().length >= 2 ? (
                                        <div className="p-4 pt-0">
                                            {isSearching && (
                                                <div className="flex justify-center py-6">
                                                    <Image
                                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/Loader2.svg`}
                                                        alt="loader"
                                                        width={24}
                                                        height={24}
                                                        className="animate-spin"                                        
                                                        />
                                                </div>
                                            )}
                                            {!isSearching && (
                                                <div className="space-y-1">
                                                    {searchResultBrands.length > 0 && (
                                                        <div className="mb-2">
                                                            {searchResultModels.length > 0 && (
                                                                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 px-3">Brands</div>
                                                            )}
                                                            {searchResultBrands.map((brand) => (
                                                                <button
                                                                    key={brand._id}
                                                                    type="button"
                                                                    onClick={() => handleBrandSelect(brand)}
                                                                    className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-cyan-50 cursor-pointer text-left"
                                                                >
                                                                    <span className="text-sm font-medium text-gray-800">{brand.name}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {searchResultModels.length > 0 && (
                                                        <div className="mb-2">
                                                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 px-3">Models</div>
                                                            {searchResultModels.map((model) => (
                                                                <button
                                                                    key={model._id}
                                                                    type="button"
                                                                    onClick={() => handleSearchModelSelect(model)}
                                                                    className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-cyan-50 cursor-pointer text-left"
                                                                >
                                                                    <span className="text-sm font-medium text-gray-800">
                                                                        {model?.brand_name} {model.name}
                                                                    </span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {searchResultBrands.length === 0 && searchResultModels.length === 0 && (
                                                        <p className="text-center text-gray-500 py-6 text-sm">No results found</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            {popularBrands.length > 0 && (
                                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 pt-0">
                                                    {popularBrands.map((brand) => (
                                                        <button
                                                            key={brand._id}
                                                            type="button"
                                                            onClick={() => handleBrandSelect(brand)}
                                                            className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition"
                                                            data-testid={`emi-calculator-popup-brand`}
                                                        >
                                                            {brand.image ? (
                                                                <ImageWrapper
                                                                    src={brand.image}
                                                                    alt={brand.name}
                                                                    height={64}
                                                                    width={100}
                                                                    className="object-contain h-[40px] w-auto"
                                                                />
                                                            ) : (
                                                                <div className="flex h-[40px] w-[64px] items-center justify-center rounded-lg bg-gray-100 text-[10px] font-semibold text-gray-500">
                                                                    {brand.name.slice(0, 2).toUpperCase()}
                                                                </div>
                                                            )}
                                                            <p className="text-[10px] md:text-[13px] text-center whitespace-normal w-full mt-1 font-medium">
                                                                {brand.name}
                                                            </p>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="py-4 px-2 pt-0">
                                                <div className="text-sm text-gray-600 py-2 mb-2 font-semibold mt-2 border-b border-gray-200">All Brands</div>
                                                <div className="mt-3">
                                                    {allBrands.map((brand) => (
                                                        <button
                                                            key={brand._id}
                                                            type="button"
                                                            onClick={() => handleBrandSelect(brand)}
                                                            className="w-full flex items-center gap-3 px-1 py-3 rounded hover:bg-cyan-50 cursor-pointer border-b border-gray-100 text-left"
                                                        >
                                                            <span className="text-[11px] md:text-sm font-medium text-gray-800">{brand.name}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            {/* === MODEL STEP === */}
                            {!isLoading && !errorMessage && step === "model" && (
                                <div className="px-1 py-4">
                                    <div className="relative mb-3">                                        
                                        <Image
                                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/search.svg`} 
                                            alt="search"
                                            title="search"
                                            width={18} 
                                            height={18}
                                            className="absolute left-3 top-2 text-[#99A1AF] w-[18px] h-[18px] z-10 invert-[60%] brightness-[70%]"
                                        />
                                        <input
                                            type="text"
                                            value={modelSearch}
                                            onChange={(event) => setModelSearch(event.target.value)}
                                            placeholder="Search models..."
                                            className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                                        />
                                    </div>
                                    <div>
                                        {filteredModels.length > 0 ? (
                                            filteredModels.map((model) => (
                                                <button
                                                    key={model._id}
                                                    type="button"
                                                    onClick={() => handleModelSelect(model)}
                                                    className="px-2 py-3 rounded-md hover:bg-cyan-50 cursor-pointer font-medium text-sm flex items-center gap-3 transition border-b border-gray-100 w-full text-left"
                                                    data-testid={`emi-calculator-popup-model`}
                                                >
                                                    <span>{model.name}</span>
                                                </button>
                                            ))
                                        ) : (
                                            <p className="text-center text-gray-500 py-6 text-sm">
                                                No models matched your search.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* === VARIANT STEP === */}
                            {!isLoading && !errorMessage && step === "variant" && (
                                <div className="px-1 py-4">
                                    <div className="relative mb-3">
                                        
                                        <Image
                                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/search.svg`} 
                                            alt="search"
                                            title="search"
                                            width={18} 
                                            height={18}
                                            className="absolute left-3 top-2 text-[#99A1AF] w-[18px] h-[18px] z-10 invert-[60%] brightness-[70%]"
                                        />
                                        <input
                                            type="text"
                                            value={variantSearch}
                                            onChange={(event) => setVariantSearch(event.target.value)}
                                            placeholder="Search variants..."
                                            className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                                        />
                                    </div>
                                    <div>
                                        {filteredVariants.length > 0 ? (
                                            filteredVariants.map((variant) => (
                                                <button
                                                    key={variant._id}
                                                    type="button"
                                                    onClick={() => handleVariantSelect(variant._id)}
                                                    className="px-2 py-3 rounded-md hover:bg-cyan-50 cursor-pointer font-medium text-sm transition border-b border-gray-100 w-full text-left"
                                                    data-testid={`emi-calculator-popup-variant`}
                                                >
                                                    <div className="flex justify-between items-center gap-2">
                                                        <span className="flex items-center gap-1.5 flex-wrap">
                                                            <span className="font-medium text-sm text-gray-800">{variant.name}</span>
                                                            {variant.is_base_variant && (
                                                                <span className="inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700 leading-none">Base</span>
                                                            )}
                                                            {variant.is_top_variant && (
                                                                <span className="inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700 leading-none">Top</span>
                                                            )}
                                                        </span>
                                                        <span className="text-cyan-600 font-semibold text-[12px] shrink-0">
                                                            {formatPrice(variant.price)}
                                                        </span>
                                                    </div>
                                                    {getVariantMeta(variant) && (
                                                        <div className="text-xs text-gray-500 mt-1">{getVariantMeta(variant)}</div>
                                                    )}
                                                </button>
                                            ))
                                        ) : (
                                            <p className="text-center text-gray-500 py-6 text-sm">
                                                No variants matched your search.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}